import React, { useContext, useState } from "react";
import Form from "../../../ui/Form/Form";
import AddRange from "../../../ui/range/AddRange";
import TabPanel from "../../../ui/tabs/TabPanel";
import UpdateExistingRange from "../../../ui/range/UpdateExistingRange";
import { errorAlert, makeApiRequest, successAlert } from "../../../util";
import AuthContext from "../../../store/authContext";
import InfoAlert from "../../../ui/InfoAlert";
import { IUpdateGroupRanges } from "../../../types";
import {
  CREATE_XSSI_CONNECTION_TRANSLATIONS,
  EDIT_XSSI_CONNECTION_TRANSLATIONS,
} from "../../../translations/util";

const getAPINameFromGroupName = (groupName: string) => {
  if (groupName === "IWF") {
    return "iwf-group";
  }
  return "radio-group";
};

const getHeadingNameFromGroupName = (groupName: string) => {
  if (groupName === "IWF") {
    return EDIT_XSSI_CONNECTION_TRANSLATIONS.range.heading.iwfGroup;
  }
  return EDIT_XSSI_CONNECTION_TRANSLATIONS.range.heading.radioGroup;
};

const UpdateGroupRanges = ({
  ranges,
  xssiRef,
  org,
  refetchConnection,
  hasEditPermission,
  hasDeletePermission,
  groupName,
}: IUpdateGroupRanges) => {
  const { token } = useContext(AuthContext);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const addToRange = async (min: number, max: number) => {
    // The range type (unit, iwf-group or radio-group).
    setAddLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/xssi-connection-ranges/${xssiRef}/range/${getAPINameFromGroupName(
          groupName
        )}`,
        method: "POST",
        body: JSON.stringify({
          "MIN-ID": +min,
          "MAX-ID": +max,
        }),
        token,
        org,
        emptyResponse: true,
      });
      successAlert(EDIT_XSSI_CONNECTION_TRANSLATIONS.range.successAlertMessage);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setAddLoading(false);
    }
  };

  const deleteRange = (idx: number) => async () => {
    setDeleteLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/xssi-connection-ranges/${xssiRef}/range/${getAPINameFromGroupName(
          groupName
        )}/${idx}`,
        method: "DELETE",
        token,
        org,
        emptyResponse: true,
      });
      successAlert(
        EDIT_XSSI_CONNECTION_TRANSLATIONS.range.deleteSuccessAlertMessage
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setDeleteLoading(false);
    }
  };

  const updateRange = async (min: number, max: number, id: number) => {
    setEditLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/xssi-connection-ranges/${xssiRef}/range/${getAPINameFromGroupName(
          groupName
        )}/${id}`,
        method: "PUT",
        body: JSON.stringify({
          "MIN-ID": +min,
          "MAX-ID": +max,
        }),
        token,
        org,
        emptyResponse: true,
      });
      successAlert(
        EDIT_XSSI_CONNECTION_TRANSLATIONS.range.editSuccessAlertMessage
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setEditLoading(false);
    }
  };

  return (
    <div className="data-tabcontent">
      <TabPanel>
        <div className="data-form">
          <Form>
            {hasEditPermission ? (
              <AddRange
                loading={addLoading}
                addRangeTitle={
                  groupName === "IWF"
                    ? CREATE_XSSI_CONNECTION_TRANSLATIONS.ranges
                        .createNewIWFRangeText
                    : CREATE_XSSI_CONNECTION_TRANSLATIONS.ranges
                        .createNewRadioRangeText
                }
                labelPrefix={
                  EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                    .groupLabelPrefix
                }
                addToRange={addToRange}
              />
            ) : null}
            <div className="data-card">
              <h3 data-cy={`${groupName}GroupRange`}>
                {getHeadingNameFromGroupName(groupName)}{" "}
                {EDIT_XSSI_CONNECTION_TRANSLATIONS.range.ranges}{" "}
              </h3>
              {ranges.length < 1 ? (
                <InfoAlert
                  heading={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.infoAlert.heading(
                    getHeadingNameFromGroupName(groupName)
                  )}
                  subText={
                    EDIT_XSSI_CONNECTION_TRANSLATIONS.range.infoAlert.subText
                  }
                />
              ) : null}
              {ranges.map((range) => {
                // eslint-disable-next-line react/no-array-index-key
                return (
                  <UpdateExistingRange
                    deleteLoading={deleteLoading}
                    editloading={editLoading}
                    key={range.ID}
                    labelPrefix={
                      EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                        .groupLabelPrefix
                    }
                    range={{
                      id: range.ID,
                      creationDate: range.CreationDate,
                      modificationDate: range.ModificationDate,
                      min: range["GROUP-MIN-ID"],
                      max: range["GROUP-MAX-ID"],
                    }}
                    deleteRange={deleteRange(range.ID)}
                    updateRange={updateRange}
                    hasEditPermission={hasEditPermission}
                    hasDeletePermission={hasDeletePermission}
                  />
                );
              })}
            </div>
          </Form>
        </div>
      </TabPanel>
    </div>
  );
};

export default UpdateGroupRanges;
